import { PathIcon } from '@mm/icons';
import classnames from 'classnames';
import React, {
  MouseEvent,
  useCallback,
  useContext,
  useEffect,
  useState,
  useMemo,
} from 'react';
import { DraggableProvided } from 'react-beautiful-dnd';
import ButtonModel, { PageButtonConfig } from '@mm/state/model/ButtonModel';
import WidgetModel from '@mm/state/model/WidgetModel';
import { OnClickElsewhere } from '../../../../components/OnClickElsewhere/OnClickElsewhere';
import {
  getNodeName,
  PathGraphContext,
} from '../../../common/PathGraph/PathGraph';
import './QuestionButton.pcss';
import { QuestionButtonActiveTabArea } from './QuestionButtonActiveTabArea/QuestionButtonActiveTabArea';
import { useSelector } from 'react-redux';
import { StoreState } from '@mm/state/store-interfaces';
import { Button, ButtonProps } from '@mm/components/src';
import { colors } from '../../../../../editor/features/beta/styles/colors';
import { isAnyModalOpen } from '@mm/state/selectors/modalSelector';

type Props = {
  quickQuestion: WidgetModel;
  button: ButtonModel;
  editMode: boolean;
  setSelectedButton: (selectedButton: number | undefined) => void;
  draggableProvided: DraggableProvided;
  isDragging: boolean;
  dialogueId?: number;
  smsOptinMode?: boolean;
};

const useIsPageIdAccessible = (pageId: number) => {
  const loadedAndNotDeletedDialogues = useSelector(
    (state: StoreState) => state.page.entities,
  );

  const isPageIdAccessible = useMemo(() => {
    return !!pageId && loadedAndNotDeletedDialogues.some(d => d.id === pageId);
  }, [pageId, loadedAndNotDeletedDialogues]);

  return {
    isPageIdAccessible,
  };
};

export const PathButton = ({
  active,
  onClick,
  css = {},
}: {
  active?: boolean;
  onClick: (e: React.MouseEvent<HTMLButtonElement>) => void;
} & Pick<ButtonProps, 'css'>) => {
  return (
    <Button
      css={{
        container: classnames('question-button__path-btn', {
          'question-button__path-btn--active': active,
        }),
        ...css,
      }}
      color="secondary-blue"
      size="sm"
      onClick={onClick}
    >
      <span className="question-button__path-icon">
        <PathIcon color={active ? 'white' : colors.accentBlue} />
      </span>
    </Button>
  );
};

export const QuestionButton = (props: Props) => {
  const {
    quickQuestion,
    button,
    editMode,
    setSelectedButton,
    draggableProvided,
    isDragging,
    dialogueId,
    smsOptinMode = false,
  } = props;
  const [localButtonName, setLocalButtonName] = useState('');
  if (!button.config) {
    button.config = {
      payload: '',
      assetId: undefined,
      uploadId: undefined,
      file: undefined,
    };
  }

  const { pathGraph, setActiveNode, deselectWidgetButtons } =
    useContext(PathGraphContext);

  const isButtonActive = !!pathGraph?.node(getNodeName(button.id, 'button'));

  const { file: imageUrl, pageId } = button.config as PageButtonConfig;

  const { isPageIdAccessible } = useIsPageIdAccessible(pageId);

  const onPathBtnClick = e => {
    e.target?.blur?.();
    deselectWidgetButtons?.(quickQuestion.buttons);

    if (!isButtonActive) {
      setActiveNode?.({
        id: button.id,
        type: 'button',
        dialogueId: (button.config as PageButtonConfig).pageId,
        action: 'open',
      });
    }
  };

  useEffect(() => {
    setLocalButtonName(button.name || '');
  }, [button.name]);

  const isTouring = useSelector(
    (store: StoreState) => store.modalState.chatbotTour.step,
  );

  const isModalOpen = useSelector(isAnyModalOpen);

  const handleClickOutside = useCallback(
    (_e: MouseEvent<HTMLDivElement>, clickStartedInsideComponent: boolean) => {
      if (
        editMode &&
        !clickStartedInsideComponent &&
        !isTouring &&
        !isModalOpen
      ) {
        setSelectedButton(undefined);
      }
    },
    [setSelectedButton, editMode, isTouring, isModalOpen],
  );

  const onClickButton = () => {
    const selectedButton = editMode ? undefined : button.id;

    deselectWidgetButtons?.(quickQuestion.buttons);

    setSelectedButton(selectedButton);
  };

  return (
    <div
      className={classnames('question-button', {
        'question-button--edit-mode': editMode && !isDragging,
        'question-button--path-selected': isButtonActive,
        'question-button--has-path': isPageIdAccessible,
      })}
    >
      {editMode && !isDragging && (
        <OnClickElsewhere handleClick={handleClickOutside}>
          <QuestionButtonActiveTabArea
            smsOptinMode={smsOptinMode}
            quickQuestion={quickQuestion}
            setSelectedButton={setSelectedButton}
            editMode={editMode}
            button={button}
            localButtonName={localButtonName}
            setLocalButtonName={setLocalButtonName}
            dialogueId={dialogueId}
          />
        </OnClickElsewhere>
      )}

      <span
        className="question-button__button-container"
        ref={draggableProvided.innerRef}
        {...draggableProvided.draggableProps}
        {...draggableProvided.dragHandleProps}
      >
        <Button
          css={{ container: 'question-button__button' }}
          onClick={onClickButton}
          color="secondary-blue"
          size="sm"
        >
          {imageUrl && (
            <img
              src={imageUrl}
              className="question-button__button-thumbnail"
              alt=""
            />
          )}

          <span className="question-button__button-text">
            {localButtonName || '...'}
          </span>
        </Button>
        {isPageIdAccessible && (
          <PathButton onClick={onPathBtnClick} active={isButtonActive} />
        )}
      </span>
    </div>
  );
};
